import { cameraControlDisabled, cameraControlLabel } from '../components/GameMenu/callControlLabels'
import {
  cameraWaitingForBackground,
  type BackgroundProblem,
} from '../services/liveKitVideoBackground'
import { useCameraBackgroundState } from './useCameraBackgroundState'
import { useCameraPermissionBlocked } from './useCameraPermissionBlocked'
import { useVideoToggleBusy } from './useVideoToggleBusy'

export interface CameraControlState {
  /** The segmentation pipeline is rebuilding: turning the camera on waits for it. */
  busy: boolean
  /** Camera access looks blocked, by either signal. Changes the words, not always the button. */
  blocked: boolean
  /** Why the chosen background is not showing, if it is not. */
  backgroundProblem: BackgroundProblem | null
  /** On, but not shown: held while its background gets ready, or kept off by its problem. */
  waiting: boolean
  disabled: boolean
  label: string
}

/**
 * SC-158: the camera button's reading, for the call bar and the picture-in-picture toolbar
 * alike, so the two cannot drift apart. The decisions themselves live in `callControlLabels`;
 * this gathers their inputs from the same window-event sources for both.
 *
 * A camera toggled "on" while the pipeline rebuilds is dropped by the connector after the UI
 * has already flipped, so "on" is disabled for that window. "Off" never is: the connector
 * takes it at any time.
 */
export function useCameraControlState(videoEnabled: boolean): CameraControlState {
  const busy = useVideoToggleBusy()
  const { blocked, confirmed: blockedConfirmed } = useCameraPermissionBlocked()
  const backgroundState = useCameraBackgroundState()
  const backgroundProblem = backgroundState.problem
  const waiting = videoEnabled && cameraWaitingForBackground(backgroundState)

  return {
    busy,
    blocked,
    backgroundProblem,
    waiting,
    disabled: cameraControlDisabled({ videoEnabled, busy, blockedConfirmed }),
    label: cameraControlLabel({
      videoEnabled,
      waitingForBackground: waiting,
      backgroundProblem: Boolean(backgroundProblem),
      busy,
      blocked,
    }),
  }
}

export default useCameraControlState
